import React from 'react';
import { Search, Filter, X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Input from '../UI/Input';
import Button from '../UI/Button';
import { AdminBalanceListParams } from '../../services/billingService';

interface AdminBalanceFiltersProps {
  filters: AdminBalanceListParams;
  onFiltersChange: (filters: AdminBalanceListParams) => void;
  onClearFilters: () => void;
}

const AdminBalanceFilters: React.FC<AdminBalanceFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  const updateFilter = (key: keyof AdminBalanceListParams, value: any) => {
    onFiltersChange({ ...filters, [key]: value, page: 1 }); // Reset to page 1 on filter change
  };

  const hasActiveFilters =
    (filters.filter_balance_status && filters.filter_balance_status !== 'all') ||
    (filters.filter_mirsal_member && filters.filter_mirsal_member !== 'all') ||
    filters.min_balance ||
    filters.max_balance ||
    filters.search ||
    filters.sort_by !== 'host_name' ||
    filters.sort_order !== 'asc';

  return (
    <div
      className={`rounded-lg border p-4 space-y-4 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Filter className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
        <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Filters & Search
        </h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            leftIcon={<X className="h-4 w-4" />}
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Search */}
      <div>
        <Input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={filters.search || ''}
          onChange={(e) => updateFilter('search', e.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />
      </div>

      {/* Filter Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Balance Status Filter */}
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Balance Status
          </label>
          <select
            value={filters.filter_balance_status || 'all'}
            onChange={(e) => updateFilter('filter_balance_status', e.target.value)}
            className={`w-full px-3 py-2 rounded-md border-2 transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                : 'bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
            }`}
          >
            <option value="all">All</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="zero">Zero</option>
          </select>
        </div>

        {/* Mirsal Member Filter */}
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Mirsal Member
          </label>
          <select
            value={filters.filter_mirsal_member || 'all'}
            onChange={(e) => updateFilter('filter_mirsal_member', e.target.value)}
            className={`w-full px-3 py-2 rounded-md border-2 transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                : 'bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
            }`}
          >
            <option value="all">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Sort By
          </label>
          <select
            value={filters.sort_by || 'host_name'}
            onChange={(e) => updateFilter('sort_by', e.target.value)}
            className={`w-full px-3 py-2 rounded-md border-2 transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                : 'bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
            }`}
          >
            <option value="host_name">Host Name</option>
            <option value="balance">Balance</option>
            <option value="event_count">Event Count</option>
            <option value="last_transaction_date">Last Transaction</option>
          </select>
        </div>

        {/* Sort Order */}
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Order
          </label>
          <select
            value={filters.sort_order || 'asc'}
            onChange={(e) => updateFilter('sort_order', e.target.value)}
            className={`w-full px-3 py-2 rounded-md border-2 transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                : 'bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
            }`}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      {/* Balance Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Min Balance (JOD)
          </label>
          <Input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={filters.min_balance || ''}
            onChange={(e) => updateFilter('min_balance', e.target.value || undefined)}
          />
        </div>
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Max Balance (JOD)
          </label>
          <Input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={filters.max_balance || ''}
            onChange={(e) => updateFilter('max_balance', e.target.value || undefined)}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminBalanceFilters;

